import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiUrl, fetchJson } from '../../apiBase';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ButtonGroup from '@mui/material/ButtonGroup';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import AddTaskIcon from '@mui/icons-material/AddTask';
import QuickHomeworkEvaluationDialog from './QuickHomeworkEvaluationDialog';

const STATUS_LABELS = {
  not_done: 'Yok',
  missing: 'Eksik',
  done: 'Tam',
};



  const __parseDate = (d) => {
    if (!d) return 0;
    const s = String(d).trim();
    let t;
    // dd.MM.yyyy
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      t = new Date(parseInt(m[3],10), parseInt(m[2],10)-1, parseInt(m[1],10)).getTime();
      return isNaN(t) ? 0 : t;
    }
    // yyyy-MM-dd
    const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m2) {
      t = new Date(parseInt(m2[1],10), parseInt(m2[2],10)-1, parseInt(m2[3],10)).getTime();
      return isNaN(t) ? 0 : t;
    }
    const dt = Date.parse(s);
    return isNaN(dt) ? 0 : dt;
  };

export default function HomeworkStatuses({ schoolId, onError, onSuccess, user }) {
  const isTeacher = !!(user && user.role === 'teacher');

  const [bucket, setBucket] = useState('active');

  const [classes, setClasses] = useState([]); // [{id,name}]
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');

  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');

  const [homeworks, setHomeworks] = useState([]);
  const [loadingHomeworks, setLoadingHomeworks] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState('');

  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [quickEvaluationOpen, setQuickEvaluationOpen] = useState(false);

  const isBulkOperationRef = useRef(false);
  const preferredHomeworkRef = useRef('');
  const authHeaders = useMemo(() => ({}), []);

  const loadClasses = useCallback(async () => {
    try {
      const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`), { headers: { ...authHeaders } });
      if (!res.ok) throw new Error('classes');
      const data = await res.json();
      const entries = (data || []).map(c => ({ id: String(c.id || ''), name: c.name || c.code || '' })).filter(c => c.name);
      entries.sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
      setClasses(entries);
      if (!selectedClass && entries.length > 0) {
        setSelectedClass(entries[0].name);
        setSelectedClassId(entries[0].id);
      }
    } catch (e) {
      onError && onError('Şubeler yüklenemedi');
    }
  }, [schoolId, authHeaders, onError, selectedClass]);

  const loadTeachers = useCallback(async () => {
    try {
      const res = await fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`), { headers: { ...authHeaders } });
      if (!res.ok) throw new Error('teachers');
      const data = await res.json();
      const list = (data || []).map(t => {
        const branch = String(t.branch || '').trim();
        const subjects = String(t.subjects || branch || '').split(',').map(s => s.trim()).filter(Boolean);
        return {
          id: String(t.id || ''),
          name: `${t.name} ${t.surname}`.trim(),
          branch,
          subjects,
          phone: String(t.phone || ''),
        };
      });
      setTeachers(list);
      if (isTeacher) {
        const mePhone = String(user?.phone || '').trim();
        const match = list.find(t => t.phone === mePhone);
        setSelectedTeacherId(match?.id || '');
      }
    } catch (e) {
      onError && onError('Öğretmenler yüklenemedi');
    }
  }, [schoolId, authHeaders, onError, isTeacher, user]);

  const loadHomeworks = useCallback(async () => {
    if (!selectedClass || !selectedTeacherId) { setHomeworks([]); setSelectedHomework(''); return; }
    setLoadingHomeworks(true);
    try {
      const q = new URLSearchParams({ teacher_id: selectedTeacherId });
      if (selectedClassId) {
        q.set('class_id', selectedClassId);
      } else if (selectedClass) {
        q.set('class_name', selectedClass);
      }
      if (bucket) q.set('bucket', bucket);
      const res = await fetchJson(apiUrl(`/admin/homeworks/${encodeURIComponent(schoolId)}?${q}`), { headers: { ...authHeaders } });
      if (!res.ok) throw new Error('homeworks');
      const data = await res.json();
      {
      let list = Array.isArray(data) ? data : [];
      list = list.slice().sort((a,b)=> (__parseDate(b.date) - __parseDate(a.date)));
      setHomeworks(list);
    }
      const list2 = Array.isArray(data) ? data.slice().sort((a,b)=> (__parseDate(b.date) - __parseDate(a.date))) : [];
      const preferredId = String(preferredHomeworkRef.current || '');
      const preferred = preferredId && list2.find((item) => String(item.id || '') === preferredId);
      setSelectedHomework(preferred ? preferredId : (list2.length ? String(list2[0].id || '') : ''));
      if (preferred) preferredHomeworkRef.current = '';
    } catch (e) {
      onError && onError('Ödevler yüklenemedi');
      setHomeworks([]);
    } finally { setLoadingHomeworks(false); }
  }, [schoolId, selectedClassId, selectedTeacherId, selectedClass, bucket, authHeaders, onError]);

  const loadStatuses = useCallback(async () => {
    if (!selectedHomework || !selectedClassId) { setItems([]); return; }
    setLoadingItems(true);
    try {
      const q = new URLSearchParams({ school_id: schoolId, homework_id: selectedHomework });
      q.set('class_id', selectedClassId);
      const res = await fetchJson(apiUrl(`/admin/homework-status?${q}`), { headers: { ...authHeaders } });
      if (!res.ok) throw new Error('status');
      const data = await res.json();
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems(list.map(x => ({
        student_id: String(x.student_id || ''),
        student_name: String(x.student_name || ''),
        class_name: String(x.class_name || ''),
        is_absent: x.is_absent === true,
        status: String(x.status || ''),
        notes: String(x.note || x.notes || ''),
      })));
    } catch (e) {
      onError && onError('Durumlar yüklenemedi');
      setItems([]);
    } finally { setLoadingItems(false); }
  }, [schoolId, selectedHomework, selectedClassId, authHeaders, onError]);

  useEffect(() => { if (schoolId && !isBulkOperationRef.current) { loadClasses(); loadTeachers(); } }, [schoolId, loadClasses, loadTeachers]);
  useEffect(() => {
    const match = classes.find(c => c.name === selectedClass);
    setSelectedClassId(match?.id || '');
  }, [classes, selectedClass]);
  useEffect(() => { if (schoolId && selectedClassId && selectedTeacherId && !isBulkOperationRef.current) loadHomeworks(); }, [schoolId, selectedClassId, selectedTeacherId, bucket, loadHomeworks]);
  useEffect(() => { if (schoolId && selectedHomework && selectedClassId && !isBulkOperationRef.current) loadStatuses(); }, [schoolId, selectedHomework, selectedClassId, loadStatuses]);

  const setRowStatus = (studentId, value) => {
    setItems(prev => prev.map(it => it.student_id === studentId ? { ...it, status: value } : it));
  };

  const saveOne = async (studentId, status) => {
    try {
      const res = await fetchJson(
        apiUrl(`/admin/homework-status/${encodeURIComponent(schoolId)}/${encodeURIComponent(selectedHomework)}/bulk`),
        { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify({ records: [{ student_id: studentId, status }] }) }
      );
      if (!res.ok) throw new Error('save');
    } catch (e) {
      onError && onError('Kaydetme başarısız');
    }
  };

  const quickSet = (studentId, status) => {
    const current = (items.find(it => it.student_id === studentId)?.status) || '';
    const nextStatus = current === status ? '' : status;
    setRowStatus(studentId, nextStatus);
    saveOne(studentId, nextStatus || null);
  };

  const bulkMarkUnsetDone = async () => {
    if (!selectedHomework || !selectedClassId) return;
    setBulkSaving(true);
    isBulkOperationRef.current = true;
    try {
      const res = await fetchJson(
        apiUrl(`/admin/homework-status/${encodeURIComponent(schoolId)}/bulk-mark-unset`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({
            homework_id: selectedHomework,
            class_id: selectedClassId,
            status: 'done',
          }),
        }
      );
      if (!res.ok) throw new Error('bulk');
      const data = await res.json().catch(() => ({}));
      await loadStatuses();
      const skippedAbsent = Number(data?.skipped_absent || 0);
      onSuccess && onSuccess(
        skippedAbsent > 0
          ? `Kayıtsızlar tamamlandı olarak işaretlendi. Devamsız ${skippedAbsent} öğrenci atlandı.`
          : 'Kayıtsızlar tamamlandı olarak işaretlendi'
      );
    } catch (e) {
      onError && onError('Toplu işaretleme başarısız');
    } finally {
      setBulkSaving(false);
      // Small delay to ensure state updates complete before re-enabling effects
      setTimeout(() => {
        isBulkOperationRef.current = false;
      }, 100);
    }
  };

  const teacherOptions = teachers.map(t => ({ value: t.id, label: `${t.name}${t.subjects.length? ' - ' + t.subjects.join(', ') : ''}` }));
  const selectedTeacherLabel = teacherOptions.find(o => o.value === selectedTeacherId)?.label || '';

  const handleQuickEvaluationCompleted = async (result) => {
    const homeworkId = String(result?.homework?.id || '');
    const nextClassId = String(result?.class_id || selectedClassId || '');
    const nextTeacherId = String(result?.teacher_id || selectedTeacherId || '');
    const nextClass = classes.find((item) => String(item.id) === nextClassId);
    if (homeworkId) preferredHomeworkRef.current = homeworkId;
    if (nextClass) setSelectedClass(nextClass.name);
    setSelectedClassId(nextClassId);
    if (nextTeacherId) setSelectedTeacherId(nextTeacherId);
    setBucket('active');

    if (nextClassId === selectedClassId && nextTeacherId === selectedTeacherId && bucket === 'active') {
      await loadHomeworks();
    }
    const evaluated = Number(result?.evaluated || 0);
    const skippedAbsent = Number(result?.skipped_absent || 0);
    onSuccess && onSuccess(
      `${evaluated} öğrencinin ödev değerlendirmesi kaydedildi ve veli bildirimleri sıraya alındı${skippedAbsent ? `. ${skippedAbsent} devamsız öğrenci atlandı.` : '.'}`
    );
  };

  return (
    <>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Ödev Değerlendirme</Typography>
            <Typography variant="body2" color="text.secondary">Kayıtlı bir ödevi seçin veya hızlı değerlendirme başlatın.</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddTaskIcon />} onClick={() => setQuickEvaluationOpen(true)}>
            Hızlı Ödev Değerlendir
          </Button>
        </Box>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={4}>
            <Autocomplete
              size="small"
              options={classes}
              getOptionLabel={(opt) => (opt?.name || '')}
              value={classes.find(c => c.name === selectedClass) || null}
              onChange={(_, v) => {
                setSelectedClass(v?.name || '');
                setSelectedClassId(v?.id || '');
              }}
              renderInput={(params) => <TextField {...params} label="Şube" placeholder="Seçin" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            {isTeacher ? (
              <TextField size="small" label="Öğretmen - Ders" value={selectedTeacherLabel} fullWidth disabled />
            ) : (
              <FormControl size="small" fullWidth>
                <InputLabel id="teacher-label">Öğretmen - Ders</InputLabel>
                <Select labelId="teacher-label" label="Öğretmen - Ders" value={selectedTeacherId} onChange={(e) => setSelectedTeacherId(e.target.value)}>
                  {teacherOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {loadingHomeworks ? (
              <Skeleton variant="rectangular" height={40} />
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="hw-label">Ödev</InputLabel>
                  <Select labelId="hw-label" label="Ödev" value={selectedHomework} onChange={(e) => setSelectedHomework(e.target.value)} disabled={!selectedTeacherId}>
                    {(homeworks || []).map(hw => (
                      <MenuItem key={hw.id} value={String(hw.id)}>
                        {`${hw.date || ''} — ${hw.description}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ToggleButtonGroup
                  size="small"
                  value={bucket}
                  exclusive
                  onChange={(_, v) => { if (v) setBucket(v); }}
                  aria-label="Ödev listesi"
                >
                  <ToggleButton value="active">Aktif</ToggleButton>
                  <ToggleButton value="archived">Arşiv</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Öğrenci Durumları</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={loadingItems || bulkSaving || !selectedHomework}
              onClick={bulkMarkUnsetDone}
            >
              Kayıtsızları Tamamladı Yap
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Not: Bu toplu işlem, bugün devamsız olarak işaretlenen öğrencileri atlar.
        </Typography>
        {loadingItems ? (
          <List>
            {Array.from({ length: 6 }).map((_, i) => (
              <ListItem key={`s-${i}`} divider>
                <ListItemText primary={<Skeleton variant="text" width="60%" />} secondary={<Skeleton variant="text" width="40%" />} />
              </ListItem>
            ))}
          </List>
        ) : items.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            {selectedHomework ? (
              <Typography>Bu şube için öğrenci bulunamadı.</Typography>
            ) : (
              <Typography>Ödev ve öğretmen seçiniz.</Typography>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {items.map((it) => {
              const s = String(it.status || '');
              return (
                <ListItem key={it.student_id} divider disableGutters sx={{
                  px: { xs: .5, sm: 1 },
                  py: 1,
                  borderLeft: it.is_absent ? '4px solid' : undefined,
                  borderColor: it.is_absent ? 'warning.main' : undefined,
                  borderRadius: it.is_absent ? 1 : undefined,
                  backgroundColor: it.is_absent
                    ? (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.12)
                    : undefined,
                }}>
                  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '.95rem', sm: '1rem' }, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {it.student_name}
                      </Typography>
                      {it.is_absent && <Chip label="Devamsız" color="warning" size="small" variant="outlined" sx={{ mt: 0.5 }} />}
                    </Box>
                    <ButtonGroup size="small" aria-label="durum" sx={{
                      borderRadius: '999px',
                      overflow: 'visible',
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: (theme) => theme.palette.divider,
                      '& .MuiButtonGroup-grouped': {
                        textTransform: 'none',
                        fontWeight: 700,
                        px: { xs: .75, sm: 1 },
                        py: .5,
                        minWidth: { xs: 56, sm: 68 },
                        borderColor: (theme) => theme.palette.divider,
                      },
                      '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                        borderRightColor: 'transparent',
                      },
                      '& .MuiButtonGroup-grouped:first-of-type': {
                        borderTopLeftRadius: 999,
                        borderBottomLeftRadius: 999,
                      },
                      '& .MuiButtonGroup-grouped:last-of-type': {
                        borderTopRightRadius: 999,
                        borderBottomRightRadius: 999,
                      },
                    }}>
                      <Tooltip title={STATUS_LABELS.not_done} placement="top">
                        <Button color="error" variant={s === 'not_done' ? 'contained' : 'outlined'} onClick={() => quickSet(it.student_id, 'not_done')} aria-label={STATUS_LABELS.not_done}>
                          {STATUS_LABELS.not_done}
                        </Button>
                      </Tooltip>
                      <Tooltip title={STATUS_LABELS['missing']} placement="top">
                        <Button color="warning" variant={s === 'missing' ? 'contained' : 'outlined'} onClick={() => quickSet(it.student_id, 'missing')} aria-label={STATUS_LABELS['missing']}>
                          {STATUS_LABELS['missing']}
                        </Button>
                      </Tooltip>
                      <Tooltip title={STATUS_LABELS['done']} placement="top">
                        <Button color="success" variant={s === 'done' ? 'contained' : 'outlined'} onClick={() => quickSet(it.student_id, 'done')} aria-label={STATUS_LABELS['done']}>
                          {STATUS_LABELS['done']}
                        </Button>
                      </Tooltip>
                    </ButtonGroup>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      <QuickHomeworkEvaluationDialog
        open={quickEvaluationOpen}
        onClose={() => setQuickEvaluationOpen(false)}
        onCompleted={handleQuickEvaluationCompleted}
        onError={onError}
        schoolId={schoolId}
        classes={classes}
        teachers={teachers}
        initialClassId={selectedClassId}
        initialTeacherId={selectedTeacherId}
        isTeacher={isTeacher}
        teacherBranch={isTeacher ? String(user?.branch || user?.subjects || '').trim() : ''}
      />
    </>
  );
}
